import React, { useState } from 'react';
import { TEXT, TEXT2, TEXT3, BLUE, SUCCESS, BORDER, FONT } from '../../shared';

const PLATFORM_COLOR = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  x: '#000000',
  email: '#0EA5E9',
  sms: '#10B981',
};

function PlatformBadge({ platform }) {
  const color = PLATFORM_COLOR[platform?.toLowerCase()] || BLUE;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, color, background: `${color}15`, border: `1px solid ${color}30`, borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
      {platform || 'MSG'}
    </span>
  );
}

function MessageRow({ msg, onReply, onMarkRead, onArchive }) {
  const [expanded, setExpanded] = useState(false);
  const isUnread = !msg.read;

  return (
    <div style={{
      borderBottom: `1px solid rgba(180,205,235,0.35)`,
      padding: '12px 0',
    }}>
      <div
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(14,165,233,0.18) 0%, rgba(14,165,233,0.08) 100%)',
          border: '1px solid rgba(14,165,233,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: 13, fontWeight: 700, color: BLUE,
        }}>
          {(msg.from || '?')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span style={{ fontSize: 13, fontWeight: isUnread ? 700 : 500, color: TEXT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
              {msg.from || 'Unknown'}
            </span>
            <PlatformBadge platform={msg.platform} />
            {isUnread && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: BLUE, flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 11, color: TEXT3, marginLeft: 'auto', flexShrink: 0 }}>{msg.ts || ''}</span>
          </div>
          {msg.subject && (
            <div style={{ fontSize: 12, fontWeight: 600, color: TEXT2, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {msg.subject}
            </div>
          )}
          <div style={{ fontSize: 12, color: TEXT3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: expanded ? 'pre-wrap' : 'nowrap' }}>
            {msg.preview || msg.body || ''}
          </div>
        </div>
      </div>
      {expanded && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10, paddingLeft: 42, flexWrap: 'wrap' }}>
          <button
            onClick={() => onReply?.(msg)}
            style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${BLUE}`, background: `${BLUE}12`, color: BLUE, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
          >
            Reply
          </button>
          {isUnread && (
            <button
              onClick={() => onMarkRead?.(msg)}
              style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(180,205,235,0.6)', background: 'rgba(255,255,255,0.55)', color: TEXT2, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: FONT }}
            >
              Mark read
            </button>
          )}
          <button
            onClick={() => onArchive?.(msg)}
            style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid rgba(180,205,235,0.6)', background: 'rgba(255,255,255,0.55)', color: TEXT3, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: FONT }}
          >
            Archive
          </button>
        </div>
      )}
    </div>
  );
}

export default function InboxWidget({ data, onAction }) {
  if (!data?.length) return null;

  const handleReply = (msg) => {
    onAction?.({
      type: 'send_message',
      text: `Reply to ${msg.from} on ${msg.platform || 'inbox'}: `,
      focus: true,
    });
  };

  const handleMarkRead = (msg) => {
    onAction?.({
      type: 'send_message',
      text: `Mark message from ${msg.from} as read.`,
    });
  };

  const handleArchive = (msg) => {
    onAction?.({
      type: 'send_message',
      text: `Archive message from ${msg.from}.`,
    });
  };

  const unreadCount = data.filter(m => !m.read).length;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.55)',
      backdropFilter: 'blur(22px)',
      WebkitBackdropFilter: 'blur(22px)',
      border: '1px solid rgba(255,255,255,0.60)',
      borderRadius: 18,
      padding: '16px 18px',
      boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 10px 28px -12px rgba(11,107,211,0.16)',
      fontFamily: FONT,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2" strokeLinecap="round">
          <path d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 700, color: TEXT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Inbox
        </span>
        {unreadCount > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: BLUE, borderRadius: 999, padding: '1px 7px', marginLeft: 2 }}>
            {unreadCount} new
          </span>
        )}
      </div>

      <div>
        {data.slice(0, 5).map((msg, i) => (
          <MessageRow
            key={msg.id || i}
            msg={msg}
            onReply={handleReply}
            onMarkRead={handleMarkRead}
            onArchive={handleArchive}
          />
        ))}
      </div>

      {data.length > 5 && (
        <button
          onClick={() => onAction?.({ type: 'navigate', view: 'inbox' })}
          style={{ marginTop: 10, width: '100%', padding: '8px', borderRadius: 10, border: '1px solid rgba(14,165,233,0.25)', background: 'rgba(14,165,233,0.06)', color: BLUE, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
        >
          View all {data.length} messages
        </button>
      )}
    </div>
  );
}
